/**
 * Self-invite detection for the voucher invite form (ADR-015 R1).
 *
 * The applicant's own address cannot be their voucher. The server enforces this
 * and returns 422 `SELF_INVITE`; this module exists so the applicant is told
 * *while typing* rather than after a failed submit, which is the lowest-friction
 * place to say it.
 *
 * ⚠️ This is a convenience, never the control. The server is authoritative:
 * `session.applicant_email` can be absent here, and a client check is trivially
 * bypassed. Keep the two rule sets in step with `canonicalizeMailbox` in the
 * platform repo (`services/digavouch/src/api/routes/vouchers.ts`).
 */

/**
 * Alternate domain names for ONE mailbox at the same provider, and the
 * canonical name each folds onto. Provider-documented account aliases: a
 * Google account answers on googlemail.com, an iCloud account on me.com and
 * mac.com, a Proton account on protonmail.com and pm.me.
 *
 * ⚠️ Microsoft is deliberately absent: `x@hotmail.com` and `x@outlook.com` are
 * two separate addresses, not one account, so folding them would match two
 * different people.
 */
const DOMAIN_ALIASES: Record<string, string> = {
  "googlemail.com": "gmail.com",
  "me.com": "icloud.com",
  "mac.com": "icloud.com",
  "protonmail.com": "proton.me",
  "pm.me": "proton.me",
};

/** Canonical names only; DOMAIN_ALIASES is applied first. */
const PLUS_ALIAS_DOMAINS = new Set([
  "gmail.com",
  "outlook.com", "hotmail.com", "live.com", "msn.com",
  "outlook.co.uk", "hotmail.co.uk", "hotmail.fr", "live.co.uk",
  "icloud.com",
  "proton.me",
  "fastmail.com",
]);

const DOT_ALIAS_DOMAINS = new Set(["gmail.com"]);

/**
 * Reduce an address to the mailbox it actually delivers to.
 *
 * Three normalisations, each scoped to what the provider documents, because no
 * two of them cover the same domains (👤 Juan, 2026-09-08):
 *
 *   - alias domains fold onto their canonical name;
 *   - `+tag` is stripped at every provider that documents it;
 *   - dots are stripped for Google only. Elsewhere `ju.an@` and `jua.n@` are
 *     two different people, and folding them would block an honest voucher.
 *
 * A domain absent from a list is compared verbatim for that rule, so
 * `j.uan+work@outlook.com` loses the tag and keeps the dot. Absence produces a
 * miss, never a false positive.
 *
 * ⚠️ Yahoo is deliberately not on the plus list: it aliases with base-name plus
 * hyphen, not `+`. Add a domain only against documented provider behaviour.
 *
 * These lists are separate from `emailRouting.ts`'s on purpose. That one
 * decides which OAuth tile to recommend; this one decides whether two strings
 * are the same person. Adding a domain to one must not silently change the
 * other.
 *
 * Returns null when there is nothing to compare. Callers must read that as
 * "unknown", never as a match.
 */
export function canonicalizeMailbox(email: unknown): string | null {
  if (typeof email !== "string") return null;

  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf("@");
  if (at <= 0 || at === trimmed.length - 1) return null;

  let local = trimmed.slice(0, at);
  // Folded first so the two rule lists need only carry canonical names.
  const domain = DOMAIN_ALIASES[trimmed.slice(at + 1)] ?? trimmed.slice(at + 1);

  if (PLUS_ALIAS_DOMAINS.has(domain)) {
    const plus = local.indexOf("+");
    if (plus === 0) return null;
    if (plus > 0) local = local.slice(0, plus);
  }

  if (DOT_ALIAS_DOMAINS.has(domain)) {
    local = local.split(".").join("");
  }

  if (local.length === 0) return null;
  return `${local}@${domain}`;
}

/**
 * True only when the two addresses are the same mailbox.
 *
 * Unknown on either side is false. The applicant is never stopped because we
 * could not parse something.
 */
export function isSelfInvite(applicantEmail: unknown, voucherEmail: unknown): boolean {
  const applicant = canonicalizeMailbox(applicantEmail);
  const voucher = canonicalizeMailbox(voucherEmail);
  if (!applicant || !voucher) return false;
  return applicant === voucher;
}

/** The one message shown for this, so the form and the toast cannot drift. */
export const SELF_INVITE_MESSAGE =
  "This is your own email address. A vouch has to come from someone else.";
